import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/login_superuser.module.css';
import authFetch from '../utils/authFetch';

function getPasswordStrength(password) {
  if (!password) return { level: 0, label: '', color: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: 'Weak', color: '#ef4444' };
  if (score === 2) return { level: 2, label: 'Fair', color: '#f97316' };
  if (score === 3) return { level: 3, label: 'Good', color: '#eab308' };
  return { level: 4, label: 'Strong', color: '#22c55e' };
}

function LoginSuperuser() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved === 'dark' || saved === null;
  });

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await authFetch(`${API_BASE_URL}/api/auth/send-otp-superuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Show OTP modal
        setShowOtpModal(true);
        setResendTimer(300); // 5 minutes countdown
        setErrors({});
      } else {
        setErrors({ 
          general: data.message || 'Invalid email or password. Please try again.' 
        });
      }
      
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ 
        general: 'Login failed. Please check your connection and try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Timer countdown for OTP
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setOtpError('');

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      while (newOtp.length < 6) newOtp.push('');
      setOtp(newOtp);
    }
  };

  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setOtpError('Please enter all 6 digits');
      return;
    }

    setIsVerifying(true);
    
    try {
      // Check if this is for forgot password flow
      const isForgotPassword = forgotPasswordEmail !== '';
      const endpoint = isForgotPassword 
        ? `${API_BASE_URL}/api/auth/verify-reset-otp-superuser`
        : `${API_BASE_URL}/api/auth/verify-otp-superuser`;
      
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: isForgotPassword ? forgotPasswordEmail : formData.email,
          otp: otpCode,
          remember: formData.remember
        })
      });

      const data = await response.json();

      if (response.ok) {
        if (isForgotPassword) {
          // For forgot password, move to reset password modal
          setShowOtpModal(false);
          setShowResetPasswordModal(true);
          setOtp(['', '', '', '', '', '']);
        } else {
          // Store user data with role
          const userData = {
            ...data.data,
            role: 'superuser'
          };
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('token', data.data.token);
          
          // Transfer login theme to main theme
          const loginTheme = localStorage.getItem('loginTheme');
          const currentTheme = loginTheme || 'dark';
          localStorage.setItem('theme', currentTheme);
          
          // Update theme in database
          try {
            await authFetch(`${API_BASE_URL}/api/update-theme`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_id: data.data.id,
                is_darkmode: currentTheme === 'dark' ? 1 : 0
              })
            });
          } catch (error) {
            console.error('Failed to update theme in database:', error);
          }
          
          // Redirect to warden homepage
          navigate('/su-homepage');
        }
      } else {
        setOtpError(data.message || 'Invalid OTP code. Please try again.');
        setOtp(['', '', '', '', '', '']);
        document.getElementById('otp-0')?.focus();
      }
      
    } catch (error) {
      console.error('OTP verification error:', error);
      setOtpError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    
    try {
      // Check if this is for forgot password flow
      const isForgotPassword = forgotPasswordEmail !== '';
      const endpoint = isForgotPassword
        ? `${API_BASE_URL}/api/auth/forgot-password-superuser`
        : `${API_BASE_URL}/api/auth/send-otp-superuser`;
      
      const requestBody = isForgotPassword
        ? { email: forgotPasswordEmail }
        : { email: formData.email, password: formData.password };
      
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        setResendTimer(300);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
        document.getElementById('otp-0')?.focus();
      } else {
        setOtpError(data.message || 'Failed to resend OTP.');
      }
      
    } catch (error) {
      console.error('Resend OTP error:', error);
      setOtpError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
    setResendTimer(0);
    // Reset forgot password email when closing OTP modal
    if (forgotPasswordEmail) {
      setForgotPasswordEmail('');
    }
  };

  const handleForgotPassword = () => {
    setShowForgotPasswordModal(true);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
  };

  const handleSendForgotPasswordOtp = async () => {
    if (!forgotPasswordEmail.trim()) {
      setForgotPasswordError('Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotPasswordEmail)) {
      setForgotPasswordError('Please enter a valid email address');
      return;
    }

    setIsSendingOtp(true);
    setForgotPasswordError('');

    try {
      const response = await authFetch(`${API_BASE_URL}/api/auth/forgot-password-superuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setShowForgotPasswordModal(false);
        setShowOtpModal(true);
        setResendTimer(300);
        setOtp(['', '', '', '', '', '']);
        setOtpError('');
      } else {
        setForgotPasswordError(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setForgotPasswordError('Failed to send OTP. Please check your connection and try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      setResetPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setResetPasswordError('Password must be at least 8 characters long');
      return;
    }

    const strength = getPasswordStrength(newPassword);
    if (strength.level < 3) {
      setResetPasswordError('Password is too weak. Use uppercase letters, numbers, and special characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetPasswordError('Passwords do not match');
      return;
    }

    setIsResettingPassword(true);
    setResetPasswordError('');

    try {
      const response = await authFetch(`${API_BASE_URL}/api/auth/reset-password-superuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          password: newPassword,
          password_confirmation: confirmPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowResetPasswordModal(false);
        setForgotPasswordEmail('');
        setNewPassword('');
        setConfirmPassword('');
        setGeneralError('');
        setShowSuccessModal(true);
      } else {
        setResetPasswordError(data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setResetPasswordError('Failed to reset password. Please check your connection and try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCloseForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
  };

  const handleCloseResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordError('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setForgotPasswordEmail('');
  };

  return (
    <div className={`${styles.loginContainer} ${isDarkMode ? styles.darkMode : styles.lightMode}`}>
      <div className={styles.loginBackground}>
        <div className={styles.animatedBg}>
          <div className={`${styles.floatingElement} ${styles.element1}`}></div>
          <div className={`${styles.floatingElement} ${styles.element2}`}></div>
          <div className={`${styles.floatingElement} ${styles.element3}`}></div>
          <div className={`${styles.floatingElement} ${styles.element4}`}></div>
          <div className={`${styles.floatingElement} ${styles.element5}`}></div>
        </div>
      </div>

      <div className={styles.loginContent}>
        <div className={styles.loginHeader}>
          <div className={styles.logoSection}>
            <img src="/BJMPRO-CAR.png" alt="BJMP Logo" className={styles.loginLogo} />
            <div className={styles.loginTitle}>
              <h1>MediCast: BJMP Health Monitoring System</h1>
              
            </div>
          </div>
        </div>

        <div className={styles.loginFormContainer}>
          <div className={styles.loginCard}>
            <div className={styles.loginHeaderText}>
              <h2>Warden Access</h2>
              <p>Sign in to your warden account</p>
            </div>


            {errors.general && (
              <div className={`${styles.errorMessage} ${styles.generalError}`}>
                {errors.general}
              </div>
            )}

            <form className={styles.loginForm} onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? styles.error : ''}
                  placeholder="Enter your email address"
                  autoComplete="email"
                />
                {errors.email && (
                  <span className={styles.errorMessage}>{errors.email}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="password">Password</label>
                <div className={styles.passwordInputContainer}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? styles.error : ''}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.88 9.88C10.1546 9.58525 10.4859 9.34884 10.8538 9.18488C11.2218 9.02091 11.6191 8.93274 12.0219 8.92563C12.4247 8.91853 12.8248 8.99262 13.1984 9.14351C13.5719 9.29439 13.9113 9.51897 14.1962 9.80385C14.4811 10.0887 14.7056 10.4281 14.8565 10.8016C15.0074 11.1752 15.0815 11.5753 15.0744 11.9781C15.0673 12.3809 14.9791 12.7782 14.8151 13.1462C14.6512 13.5141 14.4148 13.8454 14.12 14.12M9.88 9.88L14.12 14.12M9.88 9.88L3 3M14.12 14.12L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19L17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 4.23086 7.65663 6.69 6.31L9.9 4.24Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <span className={styles.errorMessage}>{errors.password}</span>
                )}
              </div>

              <div className={styles.formOptions}>
                <label className={styles.rememberMe}>
                  <input 
                    type="checkbox" 
                    name="remember" 
                    checked={formData.remember}
                    onChange={handleInputChange}
                  />
                  <span className={styles.checkmark}></span>
                  Remember me
                </label>
                
                <button 
                  type="button" 
                  className={styles.forgotPassword}
                  onClick={handleForgotPassword}
                >
                  Forgot Password?
                </button>
              </div>

              <button 
                type="submit" 
                className={`${styles.loginBtn} ${isLoading ? styles.loading : ''}`}
                disabled={isLoading}
              >
                <span className={styles.buttonText}>Sign In</span>
                {isLoading && (
                  <div className={styles.loadingSpinner}>
                    <div className={styles.spinner}></div>
                  </div>
                )}
              </button>
            </form>

          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOtpModal && (
        <div className={styles.otpModalOverlay}>
          <div className={styles.otpModal}>
            <div className={styles.otpModalHeader}>
              <h2>Enter Verification Code</h2>
              <button 
                className={styles.closeModalBtn}
                onClick={handleCloseOtpModal}
                disabled={isVerifying}
              >
                ✕
              </button>
            </div>
            
            <div className={styles.otpModalContent}>
              {generalError && (
                <div className={`${styles.errorMessage} ${styles.generalError}`}>
                  {generalError}
                </div>
              )}
              <p className={styles.otpInstruction}>
                We've sent a 6-digit verification code to<br />
                <strong>{forgotPasswordEmail || formData.email}</strong>
              </p>

              <div className={styles.otpInputContainer}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`otp-${index}`}
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    className={styles.otpInput}
                    disabled={isVerifying}
                    autoComplete="off"
                  />
                ))}
              </div>

              {otpError && (
                <div className={styles.otpError}>{otpError}</div>
              )}

              <div className={styles.otpTimer}>
                {resendTimer > 0 ? (
                  <span>Code expires in {Math.floor(resendTimer / 60)}:{String(resendTimer % 60).padStart(2, '0')}</span>
                ) : (
                  <span className={styles.expired}>Code expired</span>
                )}
              </div>

              <button 
                className={`${styles.verifyBtn} ${isVerifying ? styles.loading : ''}`}
                onClick={handleVerifyOtp}
                disabled={isVerifying || otp.join('').length !== 6}
              >
                {isVerifying ? 'Verifying...' : 'Verify & Login'}
              </button>

              <div className={styles.resendSection}>
                <span>Didn't receive the code?</span>
                <button 
                  className={styles.resendBtn}
                  onClick={handleResendOtp}
                  disabled={resendTimer > 0 || isLoading}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className={styles.otpModalOverlay}>
          <div className={styles.otpModal}>
            <div className={styles.otpModalHeader}>
              <h2>Reset Password</h2>
              <button 
                className={styles.closeModalBtn}
                onClick={handleCloseForgotPasswordModal}
                disabled={isSendingOtp}
              >
                ✕
              </button>
            </div>
            
            <div className={styles.otpModalContent}>
              {forgotPasswordError && (
                <div className={`${styles.errorMessage} ${styles.generalError}`}>
                  {forgotPasswordError}
                </div>
              )}
              <p className={styles.otpInstruction}>
                Enter your email address and we'll send you a verification code to reset your password.
              </p>

              <div className={styles.formGroup}>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => {
                    setForgotPasswordEmail(e.target.value);
                    setForgotPasswordError('');
                  }}
                  placeholder="Enter your email address"
                  className={styles.otpInput}
                  disabled={isSendingOtp}
                  autoComplete="email"
                  style={{ width: '100%', height: '50px', fontSize: '16px', textAlign: 'left', padding: '0 16px' }}
                />
              </div>

              <button 
                className={`${styles.verifyBtn} ${isSendingOtp ? styles.loading : ''}`}
                onClick={handleSendForgotPasswordOtp}
                disabled={isSendingOtp}
              >
                {isSendingOtp ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className={styles.otpModalOverlay}>
          <div className={styles.otpModal}>
            <div className={styles.otpModalHeader}>
              <h2>Create New Password</h2>
              <button 
                className={styles.closeModalBtn}
                onClick={handleCloseResetPasswordModal}
                disabled={isResettingPassword}
              >
                ✕
              </button>
            </div>
            
            <div className={styles.otpModalContent}>
              {resetPasswordError && (
                <div className={`${styles.errorMessage} ${styles.generalError}`}>
                  {resetPasswordError}
                </div>
              )}
              <p className={styles.otpInstruction}>
                Enter your new password below.
              </p>

              <div className={styles.formGroup}>
                <label htmlFor="new-password" style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px' }}>New Password</label>
                <div className={styles.passwordInputContainer}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setResetPasswordError('');
                    }}
                    placeholder="Enter new password"
                    disabled={isResettingPassword}
                    autoComplete="new-password"
                    style={{ width: '100%', background: 'rgba(15, 23, 42, 0.8)', border: '2px solid rgba(59, 130, 246, 0.2)', borderRadius: '12px', padding: '14px 50px 14px 16px', color: '#ffffff', fontSize: '1rem' }}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{ right: '14px' }}
                  >
                    {showNewPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.88 9.88C10.1546 9.58525 10.4859 9.34884 10.8538 9.18488C11.2218 9.02091 11.6191 8.93274 12.0219 8.92563C12.4247 8.91853 12.8248 8.99262 13.1984 9.14351C13.5719 9.29439 13.9113 9.51897 14.1962 9.80385C14.4811 10.0887 14.7056 10.4281 14.8565 10.8016C15.0074 11.1752 15.0815 11.5753 15.0744 11.9781C15.0673 12.3809 14.9791 12.7782 14.8151 13.1462C14.6512 13.5141 14.4148 13.8454 14.12 14.12M9.88 9.88L14.12 14.12M9.88 9.88L3 3M14.12 14.12L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19L17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 4.23086 7.65663 6.69 6.31L9.9 4.24Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="confirm-password" style={{ color: '#f1f5f9', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px' }}>Confirm Password</label>
                <div className={styles.passwordInputContainer}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setResetPasswordError('');
                    }}
                    placeholder="Confirm new password"
                    disabled={isResettingPassword}
                    autoComplete="new-password"
                    style={{ width: '100%', background: 'rgba(15, 23, 42, 0.8)', border: '2px solid rgba(59, 130, 246, 0.2)', borderRadius: '12px', padding: '14px 50px 14px 16px', color: '#ffffff', fontSize: '1rem' }}
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ right: '14px' }}
                  >
                    {showConfirmPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.88 9.88C10.1546 9.58525 10.4859 9.34884 10.8538 9.18488C11.2218 9.02091 11.6191 8.93274 12.0219 8.92563C12.4247 8.91853 12.8248 8.99262 13.1984 9.14351C13.5719 9.29439 13.9113 9.51897 14.1962 9.80385C14.4811 10.0887 14.7056 10.4281 14.8565 10.8016C15.0074 11.1752 15.0815 11.5753 15.0744 11.9781C15.0673 12.3809 14.9791 12.7782 14.8151 13.1462C14.6512 13.5141 14.4148 13.8454 14.12 14.12M9.88 9.88L14.12 14.12M9.88 9.88L3 3M14.12 14.12L21 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9.9 4.24C10.5883 4.0789 11.2931 3.99836 12 4C19 4 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19L17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 4.23086 7.65663 6.69 6.31L9.9 4.24Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button 
                className={`${styles.verifyBtn} ${isResettingPassword ? styles.loading : ''}`}
                onClick={handleResetPassword}
                disabled={isResettingPassword}
              >
                {isResettingPassword ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className={styles.modalOverlay} onClick={() => { setShowSuccessModal(false); setErrors({}); }}>
          <div className={styles.successModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.7088 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h3>Password Reset Successful</h3>
            <p>Your password has been reset successfully! You can now login with your new password.</p>
            <button className={styles.successBtn} onClick={() => { setShowSuccessModal(false); setErrors({}); }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Theme Toggle Button */}
      <button className={styles.themeToggle} onClick={toggleTheme} aria-label="Toggle theme">
        {isDarkMode ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 3V4M12 20V21M4 12H3M6.31412 6.31412L5.5 5.5M17.6859 6.31412L18.5 5.5M6.31412 17.69L5.5 18.5M17.6859 17.69L18.5 18.5M21 12H20M16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" fillOpacity="0.2"/>
          </svg>
        )}
      </button>
    </div>
  );
}

export default LoginSuperuser;
